import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TooltipItem,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { cn } from '@/lib/utils'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

interface NetOrdersData {
  month: string
  netSales: number
  cash: number
}

interface SalesChartProps {
  data: NetOrdersData[]
  title?: string
  className?: string
}

export function SalesChart({
  data,
  title = 'Net Orders and Settlements',
  className,
}: SalesChartProps) {
  const chartData = {
    labels: data.map((d) => d.month),
    datasets: [
      {
        label: 'Net Sales',
        data: data.map((d) => d.netSales),
        backgroundColor: '#14b8a6',
        borderRadius: 4,
      },
      {
        label: 'Cash',
        data: data.map((d) => d.cash),
        backgroundColor: '#8b5cf6',
        borderRadius: 4,
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'rect',
        },
      },
      tooltip: {
        callbacks: {
          label: (context: TooltipItem<'bar'>) => {
            const value = context.parsed.y ?? 0
            return `${context.dataset.label || 'Value'}: $${value.toLocaleString()}`
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: 9,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#f3f4f6',
        },
        ticks: {
          callback: (value: number | string) => {
            const numValue = typeof value === 'string' ? parseFloat(value) : value
            if (numValue >= 1000) {
              return `$${(numValue / 1000).toFixed(0)}K`
            }
            return `$${numValue}`
          },
        },
      },
    },
  }

  return (
    <div className={cn('bg-white rounded-lg border p-4', className)}>
      <h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
      <div className="h-48">
        <Bar data={chartData} options={options} />
      </div>
    </div>
  )
}
